import React from 'react';
import { classNames } from '../utils/classNames';
import { FormatOptions, NO_VALUE, ValueFormat, formatValue } from './chartFormat';
import { HATCH_FILL, HEAT_DARK_FROM, HEAT_SCALE } from './chartTokens';

// A grid of rows (hotels) and columns (months): the colour of each cell shows its value on one
// sequential blue scale, and the cell shows the value too, so the colour is never the only signal.
// A higher value is darker. The scale runs from `min` to `max`; a value outside the range takes the
// first or the last step.
// A null value is "closed" by design (a month outside the opening season): light stripes and the
// closed label. A row can carry a note instead of values (for example "No Reservations integration"),
// across all cells, with an optional action.
// The last column shows the total of the row, when the caller gives one.
// An optional total row under the grid (`footer`) shows one total per column in its own format, with no colour:
// "Total occ" in percent under an occupancy grid, or "Total revenue" in money under a grid of shares.
// The legend under the grid shows the scale and the closed key.
// On a narrow screen the grid scrolls sideways inside its frame; the first column stays.
// The heatmap knows nothing about the data: the caller gives the rows, the columns and the values.

export type HeatmapRow = {
	id: string,
	label: string,
	// A second gray line under the label (for example the area).
	sublabel?: string,
	// One value per column. null = closed.
	values?: (number | null)[],
	// The value of the last column ("Year").
	total?: number | null,
	// A text across all cells instead of values.
	note?: React.ReactNode,
};

// The total row under the grid. Its format can differ from the format of the cells.
export type HeatmapFooter = {
	label: string,
	// One value per column. null = no value ('–').
	values: (number | null)[],
	// The value of the total column ("Year").
	total?: number | null,
	format: ValueFormat,
	currency?: string,
	decimals?: number,
};

type Props = {
	columns: string[],
	rows: HeatmapRow[],
	// percent: a fraction, 0.714 → 71% · money · number
	format: ValueFormat,
	currency?: string,
	decimals?: number,
	// The value of the first and of the last colour step. Defaults 0 and 1.
	min?: number,
	max?: number,
	// The header of the first column. Default 'Hotel'.
	rowHeader?: string,
	// The header of the total column. Without it, no total column.
	totalHeader?: string,
	// The text of a closed cell. Default 'Closed'.
	closedLabel?: string,
	// The name of the grid for a screen reader.
	ariaLabel: string,
	// The text when there is no row.
	emptyLabel?: string,
	// A total row under the grid, in bold text with no colour.
	footer?: HeatmapFooter,
};

export const heatStep = (value: number, min: number, max: number): number => {
	if (max <= min) return HEAT_SCALE.length - 1;
	const t = (value - min) / (max - min);
	return Math.min(HEAT_SCALE.length - 1, Math.max(0, Math.floor(t * HEAT_SCALE.length)));
};

const Heatmap: React.FunctionComponent<Props> = ({
	columns, rows, format, currency, decimals = 0, min = 0, max = 1, rowHeader = 'Hotel', totalHeader,
	closedLabel = 'Closed', ariaLabel, emptyLabel = 'No data for this period.', footer,
}) => {
	const options: FormatOptions = { currency, decimals };
	const minText = formatValue(min, format, options);
	const maxText = formatValue(max, format, options);

	if (rows.length === 0) {
		return <p className="py-8 text-center text-sm text-gray-500">{emptyLabel}</p>;
	}

	return <div role="figure" aria-label={ariaLabel}>
		<div className="relative overflow-x-auto">
			<table className="w-full border-separate border-spacing-0.5">
				<caption className="sr-only">{ariaLabel}</caption>
				<thead>
					<tr>
						<th scope="col" className="sticky left-0 z-10 bg-white pb-1.5 pr-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
							{rowHeader}
						</th>
						{columns.map(column => <th key={column} scope="col" className="pb-1.5 text-center text-xs font-semibold text-gray-500">{column}</th>)}
						{totalHeader !== undefined && <th scope="col" className="pb-1.5 pl-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">{totalHeader}</th>}
					</tr>
				</thead>
				<tbody>
					{rows.map(row => <tr key={row.id}>
						<th scope="row" className="sticky left-0 z-10 bg-white py-0.5 pr-2 text-left font-normal">
							<span className="block whitespace-nowrap text-sm font-semibold text-gray-900">{row.label}</span>
							{row.sublabel !== undefined && <span className="block text-xs text-gray-500">{row.sublabel}</span>}
						</th>
						{row.note !== undefined
							? <td colSpan={columns.length + (totalHeader !== undefined ? 1 : 0)}>
								<div className="flex h-9 items-center rounded border border-dashed border-gray-300 px-3 text-xs text-gray-500">{row.note}</div>
							</td>
							: <>
								{columns.map((column, i) => {
									const value = row.values?.[i] ?? null;
									if (value === null) {
										return <td key={column}>
											<div className="flex h-9 min-w-12 items-center justify-center rounded text-xs text-gray-400" style={{ background: HATCH_FILL }}>
												<span className="sr-only">{column}: </span>{closedLabel}
											</div>
										</td>;
									}
									const step = heatStep(value, min, max);
									return <td key={column}>
										<div className={classNames('flex h-9 min-w-12 items-center justify-center rounded text-xs font-semibold tabular-nums',
											step >= HEAT_DARK_FROM ? 'text-white' : 'text-gray-900')}
										style={{ background: HEAT_SCALE[step] }}>
											<span className="sr-only">{column}: </span>{formatValue(value, format, options)}
										</div>
									</td>;
								})}
								{totalHeader !== undefined && <td className="pl-3 text-right text-sm font-bold tabular-nums text-gray-900">
									{row.total === null || row.total === undefined ? NO_VALUE : formatValue(row.total, format, options)}
								</td>}
							</>}
					</tr>)}
				</tbody>
				{footer !== undefined && <tfoot>
					<tr>
						<th scope="row" className="sticky left-0 z-10 bg-white pr-2 pt-2 text-left text-sm font-semibold text-gray-900">{footer.label}</th>
						{columns.map((column, i) => {
							const value = footer.values[i] ?? null;
							return <td key={column} className="pt-2 text-center text-xs font-semibold tabular-nums text-gray-900">
								<span className="sr-only">{column}: </span>
								{value === null ? NO_VALUE : formatValue(value, footer.format, { currency: footer.currency, decimals: footer.decimals })}
							</td>;
						})}
						{totalHeader !== undefined && <td className="pl-3 pt-2 text-right text-sm font-bold tabular-nums text-gray-900">
							{footer.total === null || footer.total === undefined ? NO_VALUE
								: formatValue(footer.total, footer.format, { currency: footer.currency, decimals: footer.decimals })}
						</td>}
					</tr>
				</tfoot>}
			</table>
		</div>
		<div aria-hidden="true" className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-500">
			<span className="inline-flex items-center gap-x-2">
				{minText}
				<span className="inline-flex overflow-hidden rounded-sm">
					{HEAT_SCALE.map(color => <span key={color} className="inline-block h-2.5 w-5" style={{ background: color }} />)}
				</span>
				{maxText}
			</span>
			<span className="inline-flex items-center gap-x-2">
				<span className="inline-block h-2.5 w-5 rounded-sm ring-1 ring-inset ring-gray-200" style={{ background: HATCH_FILL }} />
				{closedLabel}
			</span>
		</div>
	</div>;
};

export default Heatmap;
